import { ControlledIndexTable, ControlledIndexTableProps, PeriodValue } from '@shinetools/lumen-react'
import { ReactElement } from 'react'

import { BillsTableColumn } from './types/billsTableColumn'
import { BillsTableRow } from './types/billsTableRow'
import { BillsTableSortCriterion } from './types/billsTableSortCriterion'
import { isBillsTableSortCriterion } from './utils/isBillsTableSortCriterion'

type ExtendedControlledIndexTableProps = Omit<
  ControlledIndexTableProps<BillsTableRow>,
  | 'columns'
  | 'pageSizeOptions'
  | 'loading'
  | 'sortCriteria'
  | 'onSortCriteriaChange'
  | 'onQueryChange'
  | 'query'
  | 'searchable'
  | 'onFilterClick'
  | 'quickFilters'
  | 'filterCount'
  | 'period'
>

export interface BillsTableProps extends ExtendedControlledIndexTableProps {
  columns: BillsTableColumn[]
  period: PeriodValue | null
  pageSizeOptions: readonly number[]
  isLoading?: boolean
  searchValue: string
  sortCriteria?: BillsTableSortCriterion | null
  onSearchChange: (searchValue: string) => void
  onPeriodChange: (period: PeriodValue | null) => void
  onSortCriteriaChange?: (sortCriteria: BillsTableSortCriterion | null) => void
}

export const BillsTable = ({
  columns,
  period,
  isLoading,
  onSortCriteriaChange,
  pageSizeOptions,
  searchValue,
  sortCriteria,
  onSearchChange,
  onPeriodChange,
  ...rest
}: BillsTableProps): ReactElement => {
  const handlePeriodChange = (newPeriod: PeriodValue | null) => {
    /**
     * The fiscal year panel has no "All" option, so re-selecting the active year
     * is the only way to clear the selection and go back to "All".
     */
    const isActiveYearReselected =
      newPeriod?.mode === 'fiscalYear' && period?.mode === 'fiscalYear' && newPeriod.year === period.year

    onPeriodChange(isActiveYearReselected ? null : newPeriod)
  }

  const formattedPageSizeOptions = pageSizeOptions?.map<
    NonNullable<ControlledIndexTableProps['pageSizeOptions']>[number]
  >((size) => ({
    value: size.toString(),
    label: size.toString(),
  }))

  return (
    <ControlledIndexTable
      {...rest}
      columns={columns}
      loading={isLoading}
      searchable
      query={searchValue}
      onQueryChange={onSearchChange}
      period={{
        mode: 'fiscalYear',
        value: period,
        onChange: handlePeriodChange,
      }}
      /**
       * ControlledIndexTable supports multi-criteria sort by default with no opt-out.
       * We only support single-criteria sort, thus we only pass the last sort criterion.
       */
      onSortCriteriaChange={(sortCriteria) => {
        const sortCriterion = sortCriteria.at(-1)
        onSortCriteriaChange?.(isBillsTableSortCriterion(sortCriterion) ? sortCriterion : null)
      }}
      sortCriteria={sortCriteria ? [sortCriteria] : []}
      pageSizeOptions={formattedPageSizeOptions}
    />
  )
}
